// components/StrategyLogsTable.jsx
import { useState, Fragment } from "react";
import { useStrategyLogs } from "../../hooks/useStrategyLogs";
import "./Logs.css";

const EVENT_CLASS = {
  ENTRY: "badge-entry",
  EXIT: "badge-exit",
  T1_HIT: "badge-t1",
  T2_HIT: "badge-t2",
  TRAIL_UP: "badge-trail",
  RED_MARKED: "badge-red",
  ARMED: "badge-armed",
  EXPIRED: "badge-expired",
  SKIP_EXTENDED: "badge-skip",
  SKIP_SIZING: "badge-skip",
  SKIP_RLEVELS: "badge-skip",
  INSERT_FAIL: "badge-fail",
  ENTRY_ALERT_FAIL: "badge-fail",
  EXIT_ALERT_FAIL: "badge-fail",
};

const LEVEL_CLASS = {
  info: "dot-info",
  warn: "dot-warn",
  error: "dot-error",
  debug: "dot-debug",
};

export default function Logs() {
  const [filters, setFilters] = useState({
    date: new Date().toISOString().slice(0, 10),
    symbol: "",
    event: "",
    level: "",
  });
  const [expanded, setExpanded] = useState(null);

  const { logs, loading, error, refetch } = useStrategyLogs(filters, {
    autoRefreshMs: 15000,
  });

  const formatTime = (ts) =>
    new Date(ts).toLocaleTimeString("en-GB", {
      timeZone: "Asia/Kolkata",
      hour12: false,
    });

  const toggleExpand = (id) => setExpanded(expanded === id ? null : id);

  return (
    <div className="logs-container">
      <h2 className="logs-title">REGMOM Strategy Logs</h2>

      {/* Filters */}
      <div className="logs-filters">
        <input
          type="date"
          value={filters.date}
          onChange={(e) => setFilters({ ...filters, date: e.target.value })}
          className="logs-input"
        />
        <input
          placeholder="Symbol"
          value={filters.symbol}
          onChange={(e) =>
            setFilters({ ...filters, symbol: e.target.value.toUpperCase() })
          }
          className="logs-input logs-input-upper"
        />
        <select
          value={filters.event}
          onChange={(e) => setFilters({ ...filters, event: e.target.value })}
          className="logs-input"
        >
          <option value="">All Events</option>
          <option>RED_MARKED</option>
          <option>ARMED</option>
          <option>ENTRY</option>
          <option>T1_HIT</option>
          <option>T2_HIT</option>
          <option>TRAIL_UP</option>
          <option>EXIT</option>
          <option>EXPIRED</option>
          <option>SKIP_EXTENDED</option>
          <option>SKIP_SIZING</option>
          <option>SKIP_RLEVELS</option>
          <option>INSERT_FAIL</option>
        </select>
        <select
          value={filters.level}
          onChange={(e) => setFilters({ ...filters, level: e.target.value })}
          className="logs-input"
        >
          <option value="">All Levels</option>
          <option value="info">Info</option>
          <option value="warn">Warn</option>
          <option value="error">Error</option>
        </select>
        <button onClick={refetch} className="logs-btn">
          Refresh
        </button>
        <span className="logs-status">
          {loading
            ? "Loading…"
            : error
              ? `Error: ${error}`
              : `${logs.length} entries`}
        </span>
      </div>

      {/* Table */}
      <div className="logs-table-wrap">
        <table className="logs-table">
          <thead>
            <tr>
              <th className="col-time">Time</th>
              <th className="col-dot"></th>
              <th className="col-symbol">Symbol</th>
              <th className="col-event">Event</th>
              <th>Message</th>
              <th className="col-data">Data</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 && !loading && !error && (
              <tr>
                <td colSpan={6} className="logs-empty">
                  No logs found for these filters
                </td>
              </tr>
            )}

            {error && (
              <tr>
                <td colSpan={6} className="logs-error">
                  Failed to load: {error}
                </td>
              </tr>
            )}

            {logs.map((log) => (
              <Fragment key={log.id}>
                <tr
                  className={log.data ? "logs-row clickable" : "logs-row"}
                  onClick={() => log.data && toggleExpand(log.id)}
                >
                  <td className="cell-time">{formatTime(log.created_at)}</td>
                  <td>
                    <span
                      className={`level-dot ${LEVEL_CLASS[log.level] || "dot-debug"}`}
                      title={log.level}
                    />
                  </td>
                  <td className="cell-symbol">
                    {log.symbol || <span className="cell-dim">—</span>}
                  </td>
                  <td>
                    <span
                      className={`event-badge ${EVENT_CLASS[log.event] || "badge-default"}`}
                    >
                      {log.event}
                    </span>
                  </td>
                  <td className="cell-msg">{log.message}</td>
                  <td className="cell-data">
                    {log.data && (
                      <span className="data-toggle">
                        {expanded === log.id ? "▲ Hide" : "▼ View"}
                      </span>
                    )}
                  </td>
                </tr>

                {expanded === log.id && log.data && (
                  <tr className="logs-expanded">
                    <td colSpan={6}>
                      <pre className="logs-json">
                        {JSON.stringify(log.data, null, 2)}
                      </pre>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {loading && logs.length > 0 && (
        <div className="logs-refreshing">Refreshing…</div>
      )}
    </div>
  );
}
